import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { keyframes } from "@mui/system";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ViewStreamRoundedIcon from "@mui/icons-material/ViewStreamRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

import Content from "../../../components/Content";
import RightSidebar from "../../../components/RightSidebar";
import { CreateEvacuationPlanSidebar } from "../../../components/evacuation/CreateEvacuationPlanSidebar";
import { useOrganisation } from "../../../hooks/useOrganisation";
import {
  useEvacuationPlansByOrganisation,
  useAlarmButtonTypes,
} from "../../../hooks/useEvacuationData";
import { EvacuationPlan } from "../../../types/evacuation";
import { styleForEmergencyType } from "../../../constants/emergencyCategories";

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(-14px, 20px, 0) scale(1.04); }
`;

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const styleForType = styleForEmergencyType;

interface StepTileProps {
  count: number;
  typeKey: string;
  size?: number;
}

const StepTile: React.FC<StepTileProps> = ({ count, typeKey, size = 52 }) => {
  const meta = styleForType(typeKey);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "14px",
        background: `linear-gradient(155deg, ${meta.color} 0%, ${meta.dark} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 1px 0 rgba(255,255,255,0.35) inset, 0 12px 26px -14px ${meta.color}88`,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 25% 20%, rgba(255,255,255,0.24), transparent 55%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          left: "14%",
          right: "14%",
          top: "26%",
          height: "1.5px",
          background: "rgba(255,255,255,0.22)",
          boxShadow:
            "0 10px 0 rgba(255,255,255,0.22), 0 20px 0 rgba(255,255,255,0.22)",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          fontSize: size * 0.42,
          lineHeight: 1,
          letterSpacing: "-0.03em",
          color: "#fff",
          position: "relative",
          zIndex: 1,
          textShadow: "0 1px 2px rgba(0,0,0,0.15)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 700,
          fontSize: size * 0.12,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.72)",
          mt: "2px",
          position: "relative",
          zIndex: 1,
        }}
      >
        STEG
      </Typography>
    </Box>
  );
};

interface CategoryChipProps {
  typeKey: string;
  label: string;
}

const CategoryChip: React.FC<CategoryChipProps> = ({ typeKey, label }) => {
  const meta = styleForType(typeKey);
  const Icon = meta.icon;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.75,
        height: 32,
        borderRadius: "999px",
        border: `1px solid ${meta.border}`,
        background: meta.tint,
        color: meta.color,
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.005em",
        fontFamily: '"DM Sans", sans-serif',
        maxWidth: 240,
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: meta.color,
          boxShadow: `0 0 0 4px ${meta.halo}`,
          flexShrink: 0,
        }}
      />
      <Icon sx={{ fontSize: 16, opacity: 0.95, flexShrink: 0 }} />
      <Box
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

interface StatusChipProps {
  active: boolean;
}

const StatusChip: React.FC<StatusChipProps> = ({ active }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1,
      px: 1.75,
      height: 32,
      borderRadius: "999px",
      border: `1px solid ${
        active ? "rgba(43,140,77,0.32)" : "rgba(10,22,40,0.12)"
      }`,
      background: active ? "rgba(43,140,77,0.1)" : "rgba(10,22,40,0.025)",
      color: active ? "#1a6536" : "rgba(95,104,119,0.75)",
      fontSize: "13px",
      fontWeight: 700,
      letterSpacing: "0.005em",
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    <Box
      sx={{
        width: 9,
        height: 9,
        borderRadius: "50%",
        background: active ? "#2b8c4d" : "#c7c3ba",
        boxShadow: active ? "0 0 0 4px rgba(43,140,77,0.18)" : "none",
        flexShrink: 0,
      }}
    />
    {active ? "Aktiv" : "Inaktiv"}
  </Box>
);

interface MetricCellProps {
  label: string;
  value: number;
  total: number;
  tint: string;
  index: number;
  suffix?: string;
}

const MetricCell: React.FC<MetricCellProps> = ({
  label,
  value,
  total,
  tint,
  index,
  suffix,
}) => {
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  return (
    <Box
      sx={{
        py: 3.5,
        pr: 3,
        pl: { xs: 0, md: index === 0 ? 0 : 3 },
        position: "relative",
        borderRight: {
          xs: "none",
          md: index < 3 ? `1px solid ${LINE}` : "none",
        },
        borderBottom: {
          xs: index < 2 ? `1px solid ${LINE}` : "none",
          md: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: INK_MUTED,
          mb: 1.5,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: "2.75rem",
            fontWeight: 500,
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
          {suffix && (
            <Box
              component="span"
              sx={{ fontSize: "0.55em", color: INK_MUTED, ml: 0.25 }}
            >
              {suffix}
            </Box>
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "2px",
          width: "100%",
          maxWidth: 180,
          background: LINE_SOFT,
          borderRadius: "2px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: tint,
            transformOrigin: "left",
            transform: `scaleX(${pct})`,
          }}
        />
      </Box>
    </Box>
  );
};

interface RowPlan {
  id: number;
  name: string;
  emergency_type_key: string;
  emergency_type_label: string;
  steps_count: number;
  is_active: boolean;
}

interface PlanRowProps {
  plan: RowPlan;
  onSelect: (id: number) => void;
}

const PlanRow: React.FC<PlanRowProps> = ({ plan, onSelect }) => (
  <Box
    onClick={() => onSelect(plan.id)}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "auto 1fr auto",
        md: "auto minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 0.8fr) auto",
      },
      alignItems: "center",
      gap: { xs: 2, md: 3 },
      px: 3,
      py: 2.5,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.3s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "14%",
        bottom: "14%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138, 17, 37, 0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
        "& .row-name": { color: BRAND },
      },
    }}
  >
    <StepTile
      count={plan.steps_count}
      typeKey={plan.emergency_type_key}
      size={52}
    />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        className="row-name"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "17px",
          fontWeight: 600,
          color: INK,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.25s ease",
        }}
      >
        {plan.name}
      </Typography>
      <Typography
        sx={{
          fontSize: "12.5px",
          color: INK_MUTED,
          mt: 0.25,
          fontFamily: '"DM Sans", sans-serif',
          letterSpacing: "0.01em",
        }}
      >
        Checklista · {plan.steps_count} steg
      </Typography>
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}>
      <CategoryChip
        typeKey={plan.emergency_type_key}
        label={plan.emergency_type_label}
      />
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" }, minWidth: 0 }}>
      <StatusChip active={plan.is_active} />
    </Box>
    <Box
      className="row-arrow"
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${LINE}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0,
        transform: "translateX(-6px)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        color: BRAND,
      }}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const PlanCard: React.FC<PlanRowProps> = ({ plan, onSelect }) => (
  <Box
    onClick={() => onSelect(plan.id)}
    sx={{
      position: "relative",
      p: 3.25,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      cursor: "pointer",
      overflow: "hidden",
      transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "48px",
        height: "2px",
        background: BRAND,
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: "rgba(10,22,40,0.18)",
        transform: "translateY(-3px)",
        boxShadow:
          "0 24px 48px -24px rgba(10,22,40,0.16), 0 2px 6px -2px rgba(10,22,40,0.04)",
        "&::before": { transform: "scaleX(1)" },
        "& .card-arrow": { transform: "translateX(3px)", color: BRAND },
      },
    }}
  >
    <Box sx={{ mb: 3 }}>
      <StepTile
        count={plan.steps_count}
        typeKey={plan.emergency_type_key}
        size={62}
      />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "20px",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.2,
        mb: 0.75,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        minHeight: "48px",
        wordBreak: "break-word",
      }}
    >
      {plan.name}
    </Typography>
    <Typography
      sx={{
        fontSize: "12.5px",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
        mb: 2.5,
      }}
    >
      Checklista · {plan.steps_count} {plan.steps_count === 1 ? "steg" : "steg"}
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
      <CategoryChip
        typeKey={plan.emergency_type_key}
        label={plan.emergency_type_label}
      />
      <StatusChip active={plan.is_active} />
    </Stack>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 2.25,
        borderTop: `1px solid ${LINE_SOFT}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: INK_MUTED,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        Öppna checklista
      </Typography>
      <ArrowForwardRoundedIcon
        className="card-arrow"
        sx={{
          fontSize: 18,
          color: INK_MUTED,
          transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

interface EmptyStateProps {
  onCreate: () => void;
  hasQuery: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onCreate, hasQuery }) => (
  <Box
    sx={{
      py: 10,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
    }}
  >
    <Box
      sx={{
        width: 68,
        height: 68,
        mx: "auto",
        mb: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
      }}
    >
      <ChecklistRoundedIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.75rem",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.025em",
        mb: 1,
      }}
    >
      {hasQuery ? "Inga träffar" : "Inga checklistor ännu"}
    </Typography>
    <Typography
      sx={{
        fontSize: "14.5px",
        color: INK_MUTED,
        maxWidth: 460,
        mx: "auto",
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {hasQuery
        ? "Prova en annan sökterm eller rensa filtren för att se alla checklistor."
        : "Skapa er första checklista för att dokumentera steg-för-steg vid olika krisscenarier — brand, PDV, bombhot eller annan nödsituation."}
    </Typography>
    {!hasQuery && (
      <Box
        component="button"
        type="button"
        onClick={onCreate}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          background: BRAND,
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.25s ease",
          boxShadow: "0 10px 24px -12px rgba(138,17,37,0.5)",
          "&:hover": {
            background: "#6b0d1d",
            transform: "translateY(-1px)",
          },
        }}
      >
        <AddRoundedIcon sx={{ fontSize: 18 }} />
        Skapa första checklistan
      </Box>
    )}
  </Box>
);

const selectSx = {
  height: 44,
  minWidth: 200,
  background: "#fff",
  fontSize: "13px",
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  fontWeight: 600,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "13px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.1,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

export const EvacuationView: React.FC = () => {
  const navigate = useNavigate();
  const { organisationId } = useOrganisation();
  const [isCreateSidebarOpen, setIsCreateSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  const { data, isLoading } = useEvacuationPlansByOrganisation(organisationId);
  const { data: alarmTypes } = useAlarmButtonTypes();

  const alarmTypeLabels = useMemo<Record<string, string>>(() => {
    if (!alarmTypes) return {};
    return alarmTypes.reduce((acc, type) => {
      acc[type.value] = type.label;
      return acc;
    }, {} as Record<string, string>);
  }, [alarmTypes]);

  const plans = useMemo<RowPlan[]>(() => {
    const raw: EvacuationPlan[] = Array.isArray(data?.data) ? data!.data : [];
    return raw.map((plan) => ({
      id: plan.id,
      name: plan.name,
      emergency_type_key: plan.emergency_type,
      emergency_type_label:
        alarmTypeLabels[plan.emergency_type] || plan.emergency_type,
      steps_count: plan.steps?.length || 0,
      is_active: !!plan.is_active,
    }));
  }, [data, alarmTypeLabels]);

  const typeOptions = useMemo(() => {
    const map = new Map<string, string>();
    plans.forEach((p) => {
      if (!map.has(p.emergency_type_key)) {
        map.set(p.emergency_type_key, p.emergency_type_label);
      }
    });
    return Array.from(map.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [plans]);

  const metrics = useMemo(() => {
    const uniqueTypeKeys = new Set<string>();
    let active = 0;
    let totalSteps = 0;
    for (const p of plans) {
      if (p.is_active) active += 1;
      totalSteps += p.steps_count;
      uniqueTypeKeys.add(p.emergency_type_key);
    }
    return {
      total: plans.length,
      active,
      totalSteps,
      uniqueTypes: uniqueTypeKeys.size,
    };
  }, [plans]);

  const filteredPlans = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans.filter((p) => {
      if (q) {
        const hay = `${p.name} ${p.emergency_type_label}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (typeFilter !== "all" && p.emergency_type_key !== typeFilter)
        return false;
      if (statusFilter === "active" && !p.is_active) return false;
      if (statusFilter === "inactive" && p.is_active) return false;
      return true;
    });
  }, [plans, search, typeFilter, statusFilter]);

  const handleSelect = (id: number): void => {
    navigate(`/admin/evacuation/details/${id}`);
  };

  const hasActiveQuery =
    !!search.trim() || typeFilter !== "all" || statusFilter !== "all";

  return (
    <Content>
      <Box
        sx={{
          position: "relative",
          px: { xs: 1, md: 2 },
          pt: 2,
          pb: 6,
          minHeight: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(138,17,37,0.09), rgba(138,17,37,0) 62%)",
            filter: "blur(8px)",
            pointerEvents: "none",
            animation: `${floatOrb} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 80,
            left: -140,
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(16,50,74,0.07), rgba(16,50,74,0) 62%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            animation: `${floatOrb} 18s ease-in-out infinite reverse`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, mb: 5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: "1.5px",
                background: `linear-gradient(90deg, ${BRAND}, rgba(138,17,37,0))`,
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: BRAND,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Organisation · Krishantering
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 2.5,
                }}
              >
                Checklistor
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 300,
                    fontStyle: "italic",
                    fontSize: "0.58em",
                    color: BRAND,
                    mt: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  & handlingsplaner
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: INK_MUTED,
                  maxWidth: 560,
                }}
              >
                Förbered er för det oväntade. Skapa steg-för-steg checklistor
                per krisscenario — brand, PDV, bombhot eller annan nödsituation
                — så att alla vet exakt vad som ska göras när larmet går.
              </Typography>
            </Box>

            <Box
              component="button"
              type="button"
              onClick={() => setIsCreateSidebarOpen(true)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                px: 2.75,
                py: 1.625,
                background: INK,
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.005em",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                boxShadow:
                  "0 14px 32px -14px rgba(10,22,40,0.45), 0 2px 4px -2px rgba(10,22,40,0.08)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${BRAND}, transparent 70%)`,
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    "0 22px 44px -18px rgba(10,22,40,0.55), 0 2px 6px -2px rgba(10,22,40,0.12)",
                  "&::before": { opacity: 1 },
                },
                "& > *": { position: "relative", zIndex: 1 },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 18 }} />
              Ny checklista
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              borderTop: `1px solid ${LINE}`,
              borderBottom: `1px solid ${LINE}`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0))",
              backdropFilter: "blur(4px)",
            }}
          >
            <MetricCell
              label="Totalt antal checklistor"
              value={metrics.total}
              total={metrics.total}
              tint={INK}
              index={0}
            />
            <MetricCell
              label="Aktiva"
              value={metrics.active}
              total={metrics.total}
              tint="#2b8c4d"
              index={1}
            />
            <MetricCell
              label="Totalt antal steg"
              value={metrics.totalSteps}
              total={metrics.total}
              tint={BRAND}
              index={2}
            />
            <MetricCell
              label="Krisscenarier"
              value={metrics.uniqueTypes}
              total={5}
              tint={NAVY}
              index={3}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              height: 44,
              minWidth: 260,
              flex: { xs: "1 1 100%", md: "0 1 320px" },
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              transition: "all 0.25s ease",
              "&:focus-within": {
                borderColor: "rgba(10,22,40,0.28)",
                boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
              },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök checklista..."
              sx={{
                flex: 1,
                fontSize: "14px",
                fontFamily: '"DM Sans", sans-serif',
                color: INK,
                "& input::placeholder": {
                  color: INK_MUTED,
                  opacity: 1,
                },
              }}
            />
          </Box>

          <FormControl>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as string)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(v) => (
                <>
                  <FilterAltRoundedIcon
                    sx={{ fontSize: 15, color: INK_MUTED }}
                  />
                  {v === "all"
                    ? "Alla scenarier"
                    : typeOptions.find((t) => t.key === v)?.label || "Alla"}
                </>
              )}
              sx={selectSx}
            >
              <MenuItem value="all">Alla scenarier</MenuItem>
              {typeOptions.map((t) => (
                <MenuItem key={t.key} value={t.key}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as string)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(v) => (
                <>
                  <BoltRoundedIcon sx={{ fontSize: 15, color: INK_MUTED }} />
                  {v === "all"
                    ? "Alla status"
                    : v === "active"
                    ? "Aktiva"
                    : "Inaktiva"}
                </>
              )}
              sx={selectSx}
            >
              <MenuItem value="all">Alla status</MenuItem>
              <MenuItem value="active">Aktiva</MenuItem>
              <MenuItem value="inactive">Inaktiva</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px !important",
                px: 1.5,
                py: 0.75,
                color: INK_MUTED,
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  background: INK,
                  color: "#fff",
                  "&:hover": { background: INK },
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewStreamRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <FormatListNumberedRoundedIcon
            sx={{ fontSize: 14, color: INK_MUTED }}
          />
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {isLoading
              ? "Läser in"
              : `Visar ${filteredPlans.length} ${
                  filteredPlans.length === 1 ? "checklista" : "checklistor"
                }${hasActiveQuery ? ` av ${plans.length}` : ""}`}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: LINE }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {isLoading ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    px: 3,
                    py: 2.5,
                    borderBottom:
                      i < 3 ? `1px solid ${LINE_SOFT}` : "none",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{ borderRadius: "14px" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="42%" height={20} />
                    <Skeleton width="22%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={140}
                    height={32}
                    sx={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={90}
                    height={32}
                    sx={{ borderRadius: "999px" }}
                  />
                </Box>
              ))}
            </Box>
          ) : filteredPlans.length === 0 ? (
            <EmptyState
              onCreate={() => setIsCreateSidebarOpen(true)}
              hasQuery={hasActiveQuery}
            />
          ) : viewMode === "list" ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
                "& > *:last-child": { borderBottom: "none" },
              }}
            >
              {filteredPlans.map((plan) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  onSelect={handleSelect}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {filteredPlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onSelect={handleSelect}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <RightSidebar
        open={isCreateSidebarOpen}
        onClose={() => setIsCreateSidebarOpen(false)}
        title="Skapa checklista"
        width={700}
      >
        <CreateEvacuationPlanSidebar
          onClose={() => setIsCreateSidebarOpen(false)}
        />
      </RightSidebar>
    </Content>
  );
};
